import { DPR, NODE_DEPTH } from '@constants/common.const';
import { NodeVisualState } from '@enums/node-states.enum';
import { NodeLayout } from '@models/layout';
import { NodeConfig, NodeView } from '@models/nodes';
import { NodeViewFactory } from './NodeViewFactory';
import { getStrategy } from './strategies';

export class NodeService {
  private currentIndex = 0;
  private outlineActive = false;
  private scene?: Phaser.Scene;
  private root?: Phaser.GameObjects.Container;
  private nodesLayer?: Phaser.GameObjects.Container;
  private rewardLayer?: Phaser.GameObjects.Container;
  private fxLayer?: Phaser.GameObjects.Container;
  private views = new Map<number, NodeView>();
  private viewFactory?: NodeViewFactory;
  private cullLastScrollY = Number.NaN;
  // Phaser doesn't frustum-cull, so hide views outside the viewport ± one
  // screen ourselves; re-check only after the camera moved this far.
  private static readonly CULL_SCROLL_THRESHOLD = 32 * DPR;

  create(
    scene: Phaser.Scene,
    layout: NodeLayout,
    config: NodeConfig,
  ): Phaser.GameObjects.Container {
    // rebuild safely
    this.destroy();

    this.scene = scene;
    this.root = scene.add.container(0, 0).setDepth(NODE_DEPTH);
    // layer for nodes
    this.nodesLayer = scene.add.container(0, 0);
    // layer for extra icons
    this.rewardLayer = scene.add.container(0, 0);
    // layer for animation
    this.fxLayer = scene.add.container(0, 0);
    this.nodesLayer.setDepth(0);
    this.rewardLayer.setDepth(1);
    this.fxLayer.setDepth(2);
    this.root.add([this.nodesLayer, this.rewardLayer, this.fxLayer]);
    this.root.sort('depth');

    this.viewFactory = new NodeViewFactory(this.rewardLayer);

    for (const node of layout.nodes) {
      const view = this.viewFactory.create(
        scene,
        node,
        config,
        this.nodesLayer,
      );
      this.views.set(node.index, view);
    }

    scene.events.on(Phaser.Scenes.Events.UPDATE, this.onSceneUpdate, this);
    this.cullLastScrollY = Number.NaN;
    this.applyCulling(true);

    return this.root;
  }

  private onSceneUpdate() {
    this.applyCulling();
  }

  private applyCulling(force = false) {
    if (!this.scene) return;

    const cam = this.scene.cameras.main;
    if (
      !force &&
      Math.abs(cam.scrollY - this.cullLastScrollY) <
        NodeService.CULL_SCROLL_THRESHOLD
    ) {
      return;
    }
    this.cullLastScrollY = cam.scrollY;

    const buffer = cam.height;
    const minY = cam.scrollY - buffer;
    const maxY = cam.scrollY + cam.height + buffer;

    for (const view of this.views.values()) {
      const y = view.sprite.y;
      view.setCulled(y < minY || y > maxY);
    }
  }

  destroy() {
    this.scene?.events.off(Phaser.Scenes.Events.UPDATE, this.onSceneUpdate, this);
    for (const v of this.views.values()) v.destroy();
    this.views.clear();
    this.viewFactory?.dispose();
    this.root?.destroy(true);
    this.root = undefined;
    this.scene = undefined;
    this.nodesLayer = undefined;
    this.rewardLayer = undefined;
    this.fxLayer = undefined;
  }

  getNodePosition(index: number) {
    const v = this.views.get(index);
    if (!v) return null;
    return { x: v.sprite.x, y: v.sprite.y };
  }

  setCurrent(currentIndex: number, prevIndex?: number) {
    // clear previous "current" visuals if we had one
    if (this.currentIndex > 0 && this.currentIndex !== prevIndex) {
      // don't force it to normal; usually it should remain passed/activated
      this.views.get(this.currentIndex)?.setState(NodeVisualState.Passed);
    }

    if (prevIndex != null && prevIndex > 0 && prevIndex !== currentIndex) {
      this.views.get(prevIndex)?.setState(NodeVisualState.Passed);
    }

    if (currentIndex > 0) {
      this.views.get(currentIndex)?.setState(NodeVisualState.Active);
      this.currentIndex = currentIndex;
    } else {
      this.currentIndex = 0;
    }
  }

  restorePassedNodes(currentIndex: number, withOutline: boolean = false) {
    // Set all nodes before the currentIndex to 'passed' state
    for (let i = 1; i < currentIndex; i++) {
      this.views.get(i)?.setState(NodeVisualState.Passed);
    }
    // Set active node
    if (currentIndex > 0) {
      this.views.get(currentIndex)?.setState(NodeVisualState.Active);
      this.currentIndex = currentIndex;

      // Restore outline animation if it was active before resize
      if (withOutline || this.outlineActive) {
        this.startNodeOutline(currentIndex);
      }
    }
  }

  clearCurrent() {
    if (this.currentIndex <= 0) return;
    this.views.get(this.currentIndex)?.setState(NodeVisualState.Passed);

    this.currentIndex = 0;
    this.outlineActive = false;
  }

  resetNodesToDefault(from: number, to: number): void {
    for (let i = from; i <= to; i++) {
      this.views.get(i)?.setState(NodeVisualState.Default);
    }
  }

  rewardConfirmedAt(index: number): void {
    const view = this.views.get(index);
    if (!view || !this.scene || !this.fxLayer) return;

    const strategy = getStrategy(view.type);
    if (strategy.onRewardConfirmed) {
      strategy.onRewardConfirmed(
        { scene: this.scene, fxLayer: this.fxLayer },
        view,
      );
    }
  }

  async flashNode(index: number, message: string): Promise<void> {
    const view = this.views.get(index);
    if (!view || !this.scene) return;

    const strategy = getStrategy(view.type);
    if (strategy.onFlashExtra) {
      await strategy.onFlashExtra(this.scene, view, message);
    }
  }

  startNodeOutline(index: number) {
    const view = this.views.get(index);
    if (!view) return;

    // Use strategy to check if outline is supported
    const strategy = getStrategy(view.type);
    if (!strategy.supportsOutline()) return;

    view.startOutline?.();
    this.outlineActive = true;
  }

  /**
   * Called when the player passes through a node without landing on it.
   * Used for marking rewards as missed, etc.
   */
  passThrough(index: number) {
    const view = this.views.get(index);
    if (!view || !this.scene || !this.fxLayer) return;

    const strategy = getStrategy(view.type);
    if (strategy.onPassThrough) {
      strategy.onPassThrough(
        { scene: this.scene, fxLayer: this.fxLayer },
        view,
      );
    }
  }

  /**
   * Called when the player lands on a node (final position after movement).
   * Used for collecting rewards, playing celebrations, etc.
   */
  landOn(index: number) {
    const view = this.views.get(index);
    if (!view || !this.scene || !this.fxLayer) return;

    const strategy = getStrategy(view.type);
    if (strategy.onLanded) {
      strategy.onLanded({ scene: this.scene, fxLayer: this.fxLayer }, view);
    }
  }
}
